import * as _ from 'lodash-es';
import type { EmitterSource, Parchment } from 'quill/core';
import Quill, { Delta } from 'quill/core';
import QuillHeader from 'quill/formats/header';
import { useInsertionEffect } from 'react';
import type { Tagged } from 'type-fest';
import { READONLY_CHANGE_EVENT } from './constants';
import Logger from '../utils/Logger';
import HeaderCollapseFormat from './formats/HeaderCollapse';
import HeaderIdFormat from './formats/HeaderId';
import * as styles from './useHeaderCollapse.module.scss';
import isInlineTextInsertion from './utils/isInlineTextInsertion';

const PREVENT_COLLAPSE_BREAKPOINT = parseInt(
  styles.preventCollapseBreakpoint,
  10,
);

export type CollapsedHeader = Tagged<QuillHeader, 'collapsed'>;

const LOCAL_COLLAPSE_ATTR_NAME = `local-${HeaderCollapseFormat.attrName}`;

const isHeaderCollapsed = (header: QuillHeader): header is CollapsedHeader => {
  const node = header.domNode;
  const localValue = node.getAttribute(LOCAL_COLLAPSE_ATTR_NAME);
  return localValue
    ? localValue === 'true'
    : !!HeaderCollapseFormat.value(node);
};

class HeaderCollapse {
  private debouncedCleanCollapseAttrs: _.DebouncedFunc<() => void>;
  private readOnly: boolean;
  private styleContainer: HTMLStyleElement;
  private localStateListeners: (() => void)[] = [];
  private styleScope: string;
  private Header: typeof QuillHeader;

  constructor(
    private quill: Quill,
    options: { styleScope: string },
  ) {
    this.styleScope = options.styleScope;
    this.Header = quill.scroll.query(
      QuillHeader.blotName,
    ) as typeof this.Header;

    this.styleContainer = document.createElement('style');

    this.quill.on(Quill.events.TEXT_CHANGE, this.handleTextChange);
    this.quill.on(READONLY_CHANGE_EVENT, this.handleReadOnlyChange);

    this.debouncedCleanCollapseAttrs = _.debounce(
      this.cleanCollapseAttrs.bind(this),
      500,
    );
    this.readOnly = !quill.isEnabled();
    this.quill.container.ownerDocument.head.appendChild(this.styleContainer);
    this.updateStyleOverrides();
  }

  destroy() {
    this.quill.off(Quill.events.TEXT_CHANGE, this.handleTextChange);
    this.quill.off(READONLY_CHANGE_EVENT, this.handleReadOnlyChange);
    this.debouncedCleanCollapseAttrs.cancel();
    this.styleContainer.remove();
    this.localStateListeners = [];
  }

  getHeadersRelativeToBlot(
    refBlot: Parchment.Blot,
    relativePosition: 'above' | 'below',
  ) {
    const flag =
      relativePosition === 'above'
        ? Node.DOCUMENT_POSITION_PRECEDING
        : Node.DOCUMENT_POSITION_FOLLOWING;

    const headers = this.quill.scroll.descendants(this.Header).filter(
      (header) =>
        refBlot.domNode.compareDocumentPosition(header.domNode) & // eslint-disable-line no-bitwise
        flag,
    );

    return relativePosition === 'above' ? headers.toReversed() : headers;
  }

  revealBlot(blot: Parchment.Blot) {
    if (!(blot.domNode instanceof HTMLElement) || blot.domNode.offsetParent) {
      return;
    }
    const headers = this.getHeadersRelativeToBlot(blot, 'above');

    let currentLevel = Infinity;
    let hasCollapseStateChanged = false;
    _.forEach(headers, (header) => {
      const level = header.statics.formats(header.domNode);
      if (level < currentLevel && isHeaderCollapsed(header)) {
        currentLevel = level;

        hasCollapseStateChanged = true;
        this.setHeaderCollapsed(header, false);
      }

      // Exit iteration early when we find the top level header
      return level > 1;
    });

    if (hasCollapseStateChanged) {
      this.updateStyleOverrides();
    }
  }

  private updateStyleOverrides() {
    let currentCollapsedLevel: number | null = null;
    const collapsedChildIndexes: number[] = [];

    const directChildren = this.quill.root.querySelectorAll(`:scope > *`);
    directChildren.forEach((child, index) => {
      const blot = this.quill.scroll.find(child);
      const { isCollapsed, level } =
        blot instanceof this.Header
          ? {
              isCollapsed: isHeaderCollapsed(blot),
              level: this.Header.formats(child),
            }
          : { isCollapsed: false, level: null };

      if (
        currentCollapsedLevel === null ||
        (level !== null && level <= currentCollapsedLevel)
      ) {
        currentCollapsedLevel = isCollapsed ? level : null;
      } else {
        collapsedChildIndexes.push(index);
      }
    });

    // Usually a header collapses multiple lines.
    // In order to shorten the selector for better performance,
    // we group the collapsed child indexes into ranges.
    const collapsedChildRanges: {
      start: number;
      end: number;
      gaps: number[];
    }[] = [];
    collapsedChildIndexes.forEach((index) => {
      const lastRange = collapsedChildRanges.at(-1);
      if (lastRange?.end === index - 1) {
        lastRange.end = index;
      } else if (lastRange?.end === index - 2) {
        lastRange.end = index;
        lastRange.gaps.push(index - 1);
      } else {
        collapsedChildRanges.push({ start: index, end: index, gaps: [] });
      }
    });

    const selector = collapsedChildRanges
      .map((range) => {
        const gapSelector = range.gaps
          .map((index) => `:not(:nth-child(${index + 1}))`)
          .join('');
        return `${this.styleScope}*:nth-child(n+${range.start + 1}):nth-child(-n+${range.end + 1})${gapSelector}`;
      })
      .join(',');

    if (selector === '') {
      this.styleContainer.textContent = '';
      return;
    }

    const styleContent = `${selector} { display: none }`;
    this.styleContainer.textContent = `@media (min-width: ${PREVENT_COLLAPSE_BREAKPOINT}px) {${styleContent}}`;
  }

  private getCollapseStateChangedHeaders(delta: Delta) {
    const { quill } = this;
    const changedHeaderIndexes: number[] = [];
    delta.reduce((index, op) => {
      if (op.delete) return index;
      const length = Delta.Op.length(op);
      if (op.retain && HeaderCollapseFormat.attrName in (op.attributes || {})) {
        changedHeaderIndexes.push(index);
      }
      return index + length;
    }, 0);

    return _.compact(
      changedHeaderIndexes.map((index) => {
        const [line] = quill.getLine(index);
        return line instanceof this.Header && line.scroll === quill.scroll
          ? line
          : null;
      }),
    );
  }

  private handleTextChange = (
    delta: Delta,
    _oldContents: Delta,
    source: EmitterSource,
  ) => {
    if (source === Quill.sources.USER) {
      this.debouncedCleanCollapseAttrs();
    }

    // In read mode, we locally override the collapse state changes
    // to prevent users from being disturbed.
    if (this.readOnly && source !== Quill.sources.USER) {
      const headers = this.getCollapseStateChangedHeaders(delta).filter(
        (header) => !header.domNode.hasAttribute(LOCAL_COLLAPSE_ATTR_NAME),
      );
      if (headers.length) {
        headers.forEach((header) => {
          header.domNode.setAttribute(
            LOCAL_COLLAPSE_ATTR_NAME,
            isHeaderCollapsed(header) ? 'false' : 'true',
          );
        });
        this.updateStyleOverrides();
        this.localStateListeners.forEach((fn) => fn());
        return;
      }
    }

    if (!isInlineTextInsertion(delta)) {
      this.updateStyleOverrides();
    }
  };

  private handleReadOnlyChange = (newReadOnly: boolean) => {
    this.readOnly = newReadOnly;

    if (!newReadOnly) {
      // Remove local collapse states when editor is changed to edit mode.
      const nodes = this.quill.root.querySelectorAll(
        `[${LOCAL_COLLAPSE_ATTR_NAME}]`,
      );
      if (nodes.length) {
        nodes.forEach((node) => node.removeAttribute(LOCAL_COLLAPSE_ATTR_NAME));
        this.updateStyleOverrides();
        this.localStateListeners.forEach((fn) => fn());
      }
    }
  };

  private cleanCollapseAttrs() {
    const { quill } = this;
    const excludeSelectors = this.Header.tagName
      .map((tagName) => `:not(${tagName})`)
      .join('');

    quill.root
      .querySelectorAll(`[${HeaderCollapseFormat.attrName}]${excludeSelectors}`)
      .forEach((node) => {
        if (node instanceof HTMLElement) {
          HeaderCollapseFormat.remove(node);
        }
      });
  }

  setHeaderCollapsed(blot: QuillHeader, collapsed = !isHeaderCollapsed(blot)) {
    if (this.readOnly) {
      const value = collapsed ? 'true' : 'false';
      blot.domNode.setAttribute(LOCAL_COLLAPSE_ATTR_NAME, value);
      this.updateStyleOverrides();
      this.localStateListeners.forEach((fn) => fn());
    } else {
      blot.format(HeaderCollapseFormat.attrName, collapsed ? 'true' : null);
    }
  }

  getCollapsedHeaders(): CollapsedHeader[] {
    return this.quill.scroll
      .descendants(this.Header)
      .filter((header) => isHeaderCollapsed(header));
  }

  subscribeToLocalCollapseStateChange(callback: () => void) {
    this.localStateListeners.push(callback);
    return () => {
      this.localStateListeners = this.localStateListeners.filter(
        (fn) => fn !== callback,
      );
    };
  }
}

const instances = new WeakMap<Quill, HeaderCollapse>();
export const getHeaderCollapse = (quill: Quill) => instances.get(quill);

const REQUIRED_FORMATS = [HeaderIdFormat, HeaderCollapseFormat];
const useHeaderCollapse = (quill: Quill | null, { skip = false } = {}) => {
  useInsertionEffect(() => {
    if (skip || !quill) return undefined;

    if (instances.has(quill)) {
      Logger.error(
        'HeaderCollapseManager already exists for this Quill instance. You might have called `useHeaderCollapse()` multiple times.',
      );
    }

    REQUIRED_FORMATS.forEach((format) => {
      if (!quill.scroll.query(format.attrName)) {
        Logger.error(
          `formats/${format.attrName} is required for HeaderCollapse.`,
        );
      }
    });

    const className = _.uniqueId('header-collapse-scope-');
    quill.container.classList.add(className);
    const instance = new HeaderCollapse(quill, {
      styleScope: `.${className} > .ql-editor >`,
    });
    instances.set(quill, instance);
    return () => {
      instances.delete(quill);
      instance.destroy();
      quill.container.classList.remove(className);
    };
  }, [skip, quill]);
};

export default useHeaderCollapse;
